import { useState, useEffect, useRef, useCallback } from 'react';

interface UseStoryPlaybackResult {
  currentIndex: number;
  progress: number;
  goNext: () => void;
  goPrev: () => void;
  pause: () => void;
  resume: () => void;
}

export function useStoryPlayback(
  slideCount: number,
  slideDuration = 5000,
  onComplete?: () => void
): UseStoryPlaybackResult {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const stateRef = useRef({ index: 0, progress: 0, paused: false });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const TICK_MS = 50;
  const increment = TICK_MS / slideDuration;

  const clearTick = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTick = useCallback(() => {
    clearTick();
    if (stateRef.current.paused) return;

    intervalRef.current = setInterval(() => {
      if (stateRef.current.paused) return;

      stateRef.current.progress += increment;

      if (stateRef.current.progress >= 1) {
        stateRef.current.progress = 0;
        const next = stateRef.current.index + 1;

        if (next >= slideCount) {
          clearTick();
          onCompleteRef.current?.();
          return;
        }

        stateRef.current.index = next;
        setCurrentIndex(next);
        setProgress(0);
      } else {
        setProgress(stateRef.current.progress);
      }
    }, TICK_MS);
  }, [slideCount, increment, clearTick]);

  useEffect(() => {
    stateRef.current = { index: 0, progress: 0, paused: false };
    setCurrentIndex(0);
    setProgress(0);
    startTick();
    return clearTick;
  }, []);

  const goNext = useCallback(() => {
    const next = stateRef.current.index + 1;
    if (next >= slideCount) {
      clearTick();
      onCompleteRef.current?.();
      return;
    }
    stateRef.current.index = next;
    stateRef.current.progress = 0;
    setCurrentIndex(next);
    setProgress(0);
    startTick();
  }, [slideCount, startTick, clearTick]);

  const goPrev = useCallback(() => {
    const prev = stateRef.current.progress > 0.2
      ? stateRef.current.index
      : Math.max(0, stateRef.current.index - 1);
    stateRef.current.index = prev;
    stateRef.current.progress = 0;
    setCurrentIndex(prev);
    setProgress(0);
    startTick();
  }, [startTick]);

  const pause = useCallback(() => {
    stateRef.current.paused = true;
    clearTick();
  }, [clearTick]);

  const resume = useCallback(() => {
    stateRef.current.paused = false;
    startTick();
  }, [startTick]);

  return { currentIndex, progress, goNext, goPrev, pause, resume };
}
